if (!customElements.get('d-icons-text-carousel')) {
  class DIconsTextCarousel extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-d-icons-viewport]');
      this.slides = Array.from(this.querySelectorAll('[data-d-icons-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-d-icons-dot]'));
      this.currentElement = this.querySelector('[data-d-icons-current]');
      this.autoplayEnabled = this.dataset.autoplay === 'true';
      this.currentIndex = 0;
      this.frame = null;
      this.timer = null;

      if (!this.viewport || this.slides.length < 2) return;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.onScroll = this.handleScroll.bind(this);
      this.onPointerEnter = this.stopAutoplay.bind(this);
      this.onPointerLeave = this.startAutoplay.bind(this);
      this.onFocusIn = this.stopAutoplay.bind(this);
      this.onFocusOut = this.handleFocusOut.bind(this);
      this.onVisibilityChange = this.handleVisibilityChange.bind(this);

      this.viewport.addEventListener('scroll', this.onScroll, { passive: true });
      this.addEventListener('mouseenter', this.onPointerEnter);
      this.addEventListener('mouseleave', this.onPointerLeave);
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);
      document.addEventListener('visibilitychange', this.onVisibilityChange);

      this.dotHandlers = this.dots.map((dot, index) => {
        const handler = () => {
          this.goTo(index);
          this.restartAutoplay();
        };
        dot.addEventListener('click', handler);
        return { dot, handler };
      });

      this.resizeObserver = new ResizeObserver(() => this.update());
      this.resizeObserver.observe(this.viewport);
      this.update();
      this.startAutoplay();
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      this.stopAutoplay();
      this.resizeObserver?.disconnect();
      this.viewport?.removeEventListener('scroll', this.onScroll);
      this.removeEventListener('mouseenter', this.onPointerEnter);
      this.removeEventListener('mouseleave', this.onPointerLeave);
      this.removeEventListener('focusin', this.onFocusIn);
      this.removeEventListener('focusout', this.onFocusOut);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      this.dotHandlers?.forEach(({ dot, handler }) => dot.removeEventListener('click', handler));
    }

    getClosestIndex() {
      return this.slides.reduce((closest, slide, index) => {
        const distance = Math.abs(slide.offsetLeft - this.viewport.scrollLeft);
        return distance < closest.distance ? { index, distance } : closest;
      }, { index: 0, distance: Number.POSITIVE_INFINITY }).index;
    }

    update() {
      this.currentIndex = this.getClosestIndex();
      this.dots.forEach((dot, index) => dot.setAttribute('aria-current', index === this.currentIndex ? 'true' : 'false'));
      if (this.currentElement) this.currentElement.textContent = String(this.currentIndex + 1);
    }

    handleScroll() {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.update());
    }

    goTo(index) {
      const targetIndex = (index + this.slides.length) % this.slides.length;
      this.viewport.scrollTo({
        left: this.slides[targetIndex].offsetLeft,
        behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
      });
    }

    startAutoplay() {
      if (!this.autoplayEnabled || this.reducedMotion?.matches || document.hidden || this.timer) return;
      this.timer = window.setInterval(() => this.goTo(this.currentIndex + 1), 3000);
    }

    stopAutoplay() {
      window.clearInterval(this.timer);
      this.timer = null;
    }

    restartAutoplay() {
      this.stopAutoplay();
      this.startAutoplay();
    }

    handleFocusOut(event) {
      if (!this.contains(event.relatedTarget)) this.startAutoplay();
    }

    handleVisibilityChange() {
      if (document.hidden) this.stopAutoplay();
      else this.startAutoplay();
    }
  }

  customElements.define('d-icons-text-carousel', DIconsTextCarousel);
}
